import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Invoice, InvoiceHttpResponse, InvoicesHttpResponse } from '../../../modals/invoice.model';
import { ProductDbService } from '../../../services/productdb.service';
import { InvoiceSuggestion } from '../models/invoice-selection.model';
export interface SalesReturnListResult {
  data: Invoice[];
  totalCount: number;
  page?: number;
  pageSize?: number;
}
@Injectable()

export class InvoiceSearchServiceService {

  // Cache for loaded invoices (Quick Search mode)
  private invoiceCache: Invoice[] = [];
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  constructor(private productService: ProductDbService) {}


    /**
   * Search invoices - routes to server or client search based on mode
   */
  searchInvoices(
    query: string | null, 
    useServerSearch: boolean
  ): Observable<Invoice[]> {
    if (useServerSearch) {
      return this.searchInvoicesServer(query);
    } else {
      return this.searchInvoicesClient(query);
    }
  }
  
  /**
   * Server-side search (Full Search mode)
   * Searches entire database via API
   */
  private searchInvoicesServer(query: string | null): Observable<Invoice[]> {
    if (!query || query.trim().length < 2) {
      return of([]);
    }
    
    return this.productService.getAllInvoice(query.trim()).pipe(
      map((response: InvoicesHttpResponse) => {
        if (response.code === '200' && response.data) {
          return response.data;
        }
        return [];
      }),
      catchError(error => {
        console.error('Server invoice search error:', error);
        return of([]);
      })
    );
  }
  
  /**
   * Client-side search (Quick Search mode)
   * Filters cached invoices
   */
  private searchInvoicesClient(query: string | null): Observable<Invoice[]> {
    // Load cache if empty or expired
    if (this.invoiceCache.length === 0 || this.isCacheExpired()) {
      return this.loadAllInvoices().pipe(
        map(invoices => this.filterInvoicesClientSide(invoices, query))
      );
    }
    
    // Use cached data
    return of(this.filterInvoicesClientSide(this.invoiceCache, query));
  }
  
  /**
   * Load all invoices from server (for Quick Search cache)
   */
  loadAllInvoices(): Observable<Invoice[]> {
    return this.productService.getAllInvoice().pipe(
      map((response: InvoicesHttpResponse) => {
        if (response.code === '200' && response.data) {
          this.invoiceCache = response.data;
          this.cacheTimestamp = Date.now();
          return this.invoiceCache;
        }
        return [];
      }),
      catchError(error => {
        console.error('Load all invoices error:', error);
        this.invoiceCache = [];
        return of([]);
      })
    );
  }

  // ===== SALES RETURN / SALE HISTORY APIs =====

  /**
   * Load sales return list — called when the Sale History (F3) tab/dialog opens
   * or when user types in the search box inside the dialog.
   *
   * Routes:
   *   GET /v1/sale/salesreturnlist          (no query → load all)
   *   GET /v1/sale/salesreturnlist?search=X (with query → filtered)
   */
  
  // loadSalesReturnList(searchQuery?: string, page?: number, pageSize?: number): Observable<SalesReturnListResult[]> {
  //   return this.productService.getSalesReturnList(searchQuery, page, pageSize).pipe(
  //     map((response: InvoicesHttpResponse) => {
  //       if (response.code === '200' && response.data) {
  //         // Only update the cache on the first page load (no pagination or page=1)
  //         if (!page || page === 1) {
  //           this.invoiceCache = response.data;
  //           this.cacheTimestamp = Date.now();
  //         }
  //         return response.data;
  //       }
  //       return [];
  //     }),
  //     catchError(error => {
  //       console.error('Sales return list load error:', error);
  //       return of([]);
  //     })
  //   );
  // }
  loadSalesReturnList(searchQuery?: string, page?: number, pageSize?: number): Observable<SalesReturnListResult> {
  return this.productService.getSalesReturnList(searchQuery, page, pageSize).pipe(
    map((response: any) => {
      if (response && response.code === '200' && response.data) {
        if (!page || page === 1) {
          this.invoiceCache = response.data;
          this.cacheTimestamp = Date.now();
        }
        // Safely check totalCount, count, total, or fall back to data length
        const total = response.totalCount ?? response.count ?? response.total ?? response.data.length;
        return {
          data: response.data,
          totalCount: total,
          page: response.page ?? page ?? 1,
          pageSize: response.pageSize ?? pageSize ?? 20
        };
      }
      return { data: [], totalCount: 0 };
    }),
    catchError(error => {
      console.error('Sales return list load error:', error);
      return of({ data: [], totalCount: 0 });
    })
  );
}


  /**
   * Fetch full invoice details by UUID — called after user selects a row in the
   * sales-return list. The response contains the invoice header + salesitems[]
   * which are then bound into the cart for the return/exchange flow.
   *
   * Route: GET /v1/sale/salesreturndetails/<uuid>
   */
  getSalesReturnDetails(uuid: string): Observable<any> {
    return this.productService.getSalesReturnDetails(uuid).pipe(
      map((response: InvoiceHttpResponse) => {
        if (response.code === '200' && response.data) {
          return response.data;
        }
        return null;
      }),
      catchError(error => {
        console.error('Sales return details error:', error);
        return of(null);
      })
    );
  }

  /**
   * Filter invoices on client side
   */
  filterInvoicesClientSide(invoices: Invoice[], query: string | null): Invoice[] {
    if (!query || query.trim().length === 0) {
      return invoices;
    }
    
    const lowerQuery = query.toLowerCase();
    return invoices.filter(invoice => 
      invoice.invoice.invoiceno.toString().toLowerCase().includes(lowerQuery) ||
      invoice.invoice.user_id.toString().toLowerCase().includes(lowerQuery)
    );
  }
  
  /**
   * Format invoices as suggestions for autocomplete
   */
  formatInvoiceSuggestions(
    invoices: Invoice[], 
    searchQuery?: string
  ): InvoiceSuggestion[] {
    if (!searchQuery) {
      return invoices.map(invoice => ({
        ...invoice,
        formattedLabel: `Inv: ${invoice.invoice.invoiceno}`
      }));
    }
    
    const lowerQuery = searchQuery.toLowerCase();
    return invoices.map(invoice => {
      let label = '';
      
      if (invoice.invoice.invoiceno.toString().toLowerCase().includes(lowerQuery)) {
        label = `${invoice.invoice.invoiceno}`;
      } else if (invoice.invoice.user_id.toString().toLowerCase().includes(lowerQuery)) {
        label = `${invoice.invoice.user_id}`;
      } else {
        label = `Inv: ${invoice.invoice.invoiceno}`;
      }
      
      return { ...invoice, formattedLabel: label };
    });
  }
  
  /**
   * Check if cache has expired
   */
  private isCacheExpired(): boolean {
    return Date.now() - this.cacheTimestamp > this.CACHE_DURATION_MS;
  }
  
  /**
   * Clear invoice cache
   */
  clearCache(): void {
    this.invoiceCache = [];
    this.cacheTimestamp = 0;
  }
  
  /**
   * Get cached invoices
   */
  getCachedInvoices(): Invoice[] {
    return this.invoiceCache;
  }
  
  /**
   * Check if invoice has been returned (all items have 0 quantity)
   */
  isInvoiceReturned(invoice: Invoice): boolean {
    const salesItems = invoice.invoice.salesitems || [];
    if (salesItems.length === 0) return false;
    
    return salesItems.every(item => !item.quantity || item.quantity === 0);
  }
  
  /**
   * Validate invoice for exchange/return
   */
  validateInvoiceForReturn(invoice: Invoice): { 
    isValid: boolean; 
    reason?: string 
  } {
    if (this.isInvoiceReturned(invoice)) {
      return {
        isValid: false,
        reason: 'This invoice has already been returned and cannot be returned again.'
      };
    }
    
    if (invoice?.invoice?.exchangestatus === 1) {
      return {
        isValid: false,
        reason: 'This invoice has already been marked as returned.'
      };
    }
    
    return { isValid: true };
  }
  
}
